import type {
  AnalyticsTrackRequest,
  AnalyticsTrackResponse,
  ClientAnalyticsEvent,
  ServiceRequestBase,
  WidgetResponsePayload,
} from "@health/types";
import type {
  ChatRequest,
  ExperienceResponse,
  GreetingRequest,
  GreetingResponse,
  HistoryResponse,
  WidgetResponseRequest,
} from "@health/types/api";
import type { UIMessage } from "ai";
import { fromTransportMessages, toTransportMessages } from "./messages";
import { readAssistantMessageStream } from "./transport";

export type ClientHeaders = HeadersInit | (() => HeadersInit | Promise<HeadersInit>);

/** Max time for a streaming request before client-side abort (90s). */
const STREAM_TIMEOUT_MS = 90_000;

export type HealthCompanionClientOptions = {
  baseUrl?: string;
  headers?: ClientHeaders;
  fetch?: typeof fetch;
};

export type SendTurnRequest = ServiceRequestBase & {
  messages: UIMessage[];
};

export type SubmitWidgetRequest = ServiceRequestBase & {
  messages: UIMessage[];
  widgetResponse: WidgetResponsePayload;
};

export type ClientHistoryResponse = Omit<HistoryResponse, "messages"> & {
  messages: UIMessage[];
};

/**
 * Canonical structured-input item shape — the value persisted to
 * `pos.answered[fieldId]` for `select_medications` / `select_allergies`.
 * Mirrors `packages/visit-orchestration/src/medical-search.ts`; declared
 * here to keep the SDK platform-neutral (no server-only deps).
 */
export type StructuredInputItem = {
  textValue: string;
  /** Opaque EMR id (typically a DoseSpot id) when EMR returned a structured match. */
  structuredId?: string;
};

function createFetchImpl(fetchImpl?: typeof fetch): typeof fetch {
  if (fetchImpl) {
    return ((input: RequestInfo | URL, init?: RequestInit) =>
      fetchImpl(input, init)) as typeof fetch;
  }

  return ((input: RequestInfo | URL, init?: RequestInit) => fetch(input, init)) as typeof fetch;
}

function trimTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

async function resolveHeaders(headers?: ClientHeaders): Promise<HeadersInit> {
  if (!headers) return {};
  return typeof headers === "function" ? await headers() : headers;
}

export class HealthCompanionClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly headers?: ClientHeaders;
  // In-flight dedupe for getExperience — same params share a single fetch.
  private experienceInflight = new Map<string, Promise<ExperienceResponse>>();

  constructor(options: HealthCompanionClientOptions = {}) {
    this.baseUrl = trimTrailingSlash(options.baseUrl ?? "");
    this.fetchImpl = createFetchImpl(options.fetch);
    this.headers = options.headers;
  }

  async *sendTurn(request: SendTurnRequest): AsyncGenerator<UIMessage, void, void> {
    const body: ChatRequest = {
      ...request,
      messages: toTransportMessages(request.messages),
    };
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), STREAM_TIMEOUT_MS);
    try {
      const response = await this.post("/v1/chat", body, abort.signal);
      yield* readAssistantMessageStream({ response });
    } finally {
      clearTimeout(timeout);
    }
  }

  async *submitWidget(request: SubmitWidgetRequest): AsyncGenerator<UIMessage, void, void> {
    const body: WidgetResponseRequest = {
      ...request,
      messages: toTransportMessages(request.messages),
    };
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), STREAM_TIMEOUT_MS);
    try {
      const response = await this.post("/v1/widget-response", body, abort.signal);
      const lastAssistant = [...request.messages]
        .reverse()
        .find((message) => message.role === "assistant");

      yield* readAssistantMessageStream({ response, lastMessage: lastAssistant });
    } finally {
      clearTimeout(timeout);
    }
  }

  async getGreeting(request: GreetingRequest): Promise<GreetingResponse> {
    return this.postJson("/v1/greeting", request);
  }

  async getHistory(options: {
    sessionId: string;
    before?: string | null;
    limit?: number;
    recapOnly?: boolean;
  }): Promise<ClientHistoryResponse> {
    const params = new URLSearchParams({
      sessionId: options.sessionId,
    });

    if (options.before) params.set("before", options.before);
    if (options.limit != null) params.set("limit", String(options.limit));
    if (options.recapOnly) params.set("recapOnly", "true");

    const history = await this.getJson<HistoryResponse>(`/v1/history?${params.toString()}`);
    return {
      ...history,
      messages: fromTransportMessages(history.messages),
    };
  }

  async deleteHistory(sessionId: string): Promise<{ ok: boolean }> {
    const params = new URLSearchParams({ sessionId });
    return this.deleteJson(`/v1/history?${params.toString()}`);
  }

  /**
   * Ship a batch of analytics events to the server. Platform-neutral — uses
   * the injected fetch + baseUrl. React consumers should use the `useTrack`
   * hook in `@health/sdk-react` which handles batching + unload flush.
   *
   * Silent on failure so analytics issues never bubble into user-facing
   * errors. Does NOT use `sendBeacon`; callers that need unload-safe delivery
   * should invoke `navigator.sendBeacon` directly (see `useTrack`).
   */
  async track(events: ClientAnalyticsEvent[]): Promise<AnalyticsTrackResponse | null> {
    if (!events.length) return null;
    const body: AnalyticsTrackRequest = { events };
    try {
      const response = await this.post("/v1/analytics/track", body);
      if (!response.ok) return null;
      return (await response.json().catch(() => null)) as AnalyticsTrackResponse | null;
    } catch {
      return null;
    }
  }

  /** Absolute URL for the analytics endpoint — used by `sendBeacon` in the hook. */
  analyticsTrackUrl(): string {
    return `${this.baseUrl}/v1/analytics/track`;
  }

  async warmCache(
    request: ServiceRequestBase,
  ): Promise<{ warmed: boolean; durationMs?: number; ttlMs?: number }> {
    return this.postJson("/v1/cache-warm", request);
  }

  /**
   * Search EMR medical-info (medications + allergies) by free-text query.
   * Used by `StructuredInputWidget` for typeahead. Server proxy enforces
   * auth, rate limiting, query length cap, and PHI-safe logging — see
   * `apps/service/src/routes/api/search.ts`.
   *
   * Failure modes are non-throwing:
   * - Network error → `{ results: [] }`
   * - 401 (no bearer) → `{ results: [] }`
   * - EMR auth failure → `{ results: [], authFailure: true }`
   * - Rate limit hit → `{ results: [], rateLimited: true }`
   *
   * Caller (widget) decides UX based on the flags; manual entry stays
   * usable in every failure mode.
   */
  async searchMedicalInfo(opts: {
    type: "medications" | "allergies";
    query: string;
    limit?: number;
  }): Promise<{
    results: StructuredInputItem[];
    authFailure?: boolean;
    rateLimited?: boolean;
  }> {
    try {
      const response = await this.post("/v1/api/search/medical-info", {
        type: opts.type,
        query: opts.query,
        ...(opts.limit != null ? { limit: opts.limit } : {}),
      });
      if (!response.ok) return { results: [] };
      const data = (await response.json().catch(() => null)) as {
        results?: StructuredInputItem[];
        authFailure?: boolean;
        rateLimited?: boolean;
      } | null;
      return {
        results: Array.isArray(data?.results) ? (data?.results ?? []) : [],
        ...(data?.authFailure ? { authFailure: true } : {}),
        ...(data?.rateLimited ? { rateLimited: true } : {}),
      };
    } catch {
      return { results: [] };
    }
  }

  async getExperience(
    request: ServiceRequestBase,
    options?: { includeAnsweredFields?: boolean },
  ): Promise<ExperienceResponse> {
    const params = new URLSearchParams({ sessionId: request.sessionId });
    if (request.variant) params.set("variant", request.variant);
    if (request.siteId) params.set("siteId", request.siteId);
    if (request.startingCategory) params.set("startingCategory", request.startingCategory);
    if (options?.includeAnsweredFields) params.set("includeAnsweredFields", "true");
    const path = `/v1/experience?${params.toString()}`;

    const existing = this.experienceInflight.get(path);
    if (existing) return existing;

    const promise = this.getJson<ExperienceResponse>(path).finally(() => {
      this.experienceInflight.delete(path);
    });
    this.experienceInflight.set(path, promise);
    return promise;
  }

  private async post(path: string, body: unknown, signal?: AbortSignal): Promise<Response> {
    const headers = await resolveHeaders(this.headers);

    return this.fetchImpl(`${this.baseUrl}${path}`, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...headers,
      },
      body: JSON.stringify(body),
      signal,
      credentials: "include",
    });
  }

  private async postJson<T>(path: string, body: unknown): Promise<T> {
    const response = await this.post(path, body);
    return this.readJson<T>(response);
  }

  private async getJson<T>(path: string): Promise<T> {
    const headers = await resolveHeaders(this.headers);
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      headers,
      method: "GET",
      credentials: "include",
    });
    return this.readJson<T>(response);
  }

  private async deleteJson<T>(path: string): Promise<T> {
    const headers = await resolveHeaders(this.headers);
    const response = await this.fetchImpl(`${this.baseUrl}${path}`, {
      headers,
      method: "DELETE",
      credentials: "include",
    });
    return this.readJson<T>(response);
  }

  private async readJson<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorText = await response.text().catch(() => "request failed");
      throw new Error(errorText || `request failed (${response.status})`);
    }

    return (await response.json()) as T;
  }
}
